import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

const LINKS = [
  { label: "About", to: "/about" },
  { label: "Welcome", to: "/welcome" },
  { label: "Table", to: "/table" },
  { label: "Restore", to: "/restore" },
  { label: "Partners", to: "/partners" },
  { label: "Membership", to: "/membership" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Alpine lake and mountains at dusk"
          className="h-full w-full object-cover img-cinematic"
        />
      </motion.div>

      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-black"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 50% at 50% 42%, rgba(0,0,0,0.78), rgba(0,0,0,0.35) 55%, transparent 82%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-bg" />

      <div
        className="relative z-10 min-h-[100svh] mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col items-center justify-center text-center py-24"
        style={{ textShadow: "0 2px 28px rgba(0,0,0,0.75)" }}
      >
        <motion.img
          src={IMAGES.logo}
          alt="Lodge & Retreat Partners"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
          data-testid="hero-logo"
          className="h-auto w-full max-w-[100%] sm:max-w-[85%] md:max-w-[72%] lg:max-w-[62%] xl:max-w-[56%] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.9)]"
          style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.8))" }}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.7 }}
          data-testid="hero-positioning"
          className="mt-6 font-serif italic text-lg md:text-xl lg:text-2xl text-metallic-gold max-w-2xl leading-snug drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
        >
          Private hospitality for extraordinary properties and unforgettable stays.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.95 }}
          className="mt-3 font-sans text-sm md:text-base text-ink/85 max-w-2xl leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]"
        >
          Get in touch to create your customised, bespoke experience today.
        </motion.p>

        <motion.nav
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.0 }}
          data-testid="hero-menu"
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
        >
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`hero-menu-${l.to.slice(1)}`}
              className="font-sans text-xs md:text-sm tracking-[0.18em] uppercase text-metallic-gold hover:opacity-60 transition-opacity duration-300"
            >
              {l.label}
            </Link>
          ))}
        </motion.nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.15 }}
        >
          <Link
            to="/book"
            data-testid="hero-book-btn"
            className="group mt-10 inline-flex items-center gap-4 rounded-full bg-black border border-gold/70 px-8 py-4 font-sans text-xs tracking-[0.2em] uppercase text-metallic-gold hover:border-gold hover:bg-black/80 transition-all duration-300"
          >
            Get in Touch for a Chat
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
